import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Send, Plus, X, Eye, PenLine } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, errorMessage } from "../../api";
import { Field, ImagePicker, useAdminRecord } from "./editorKit";

const EMPTY = {
  company: "",
  title: "",
  sector: "",
  location: "",
  logo: "",
  cover: "",
  summary: "",
  challenge: "",
  solution: "",
  results: "",
  metrics: [{ value: "", label: "" }],
  quote: { text: "", name: "", role: "" },
  featured: false,
  status: "draft",
  date: new Date().toISOString().slice(0, 10),
  slug: "",
  seoTitle: "",
  seoDescription: "",
  ogImage: "",
};
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const SECTIONS = [
  ["challenge", "The challenge", "What was slow, costly or invisible before Fleetroot? Numbered lists work well."],
  ["solution", "The solution", "Which Fleetroot modules were deployed and how they fit the operation."],
  ["results", "The results", "Outcomes with numbers. Use **bold** for the headline figures."],
];

const MarkdownField = ({ label, hint, value, onChange, testid }) => {
  const [preview, setPreview] = useState(false);
  return (
    <div className="card overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-5 h-12 border-b border-[var(--fr-border)] bg-[var(--fr-bg)]">
        <div>
          <span className="font-display font-bold text-sm">{label}</span>
          <span className="hidden md:inline text-xs text-[var(--fr-muted)] ml-3">{hint}</span>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={() => setPreview(false)} className={`h-8 px-3 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 ${!preview ? "bg-white shadow-sm" : "text-[var(--fr-muted)]"}`} data-testid={`${testid}-write`}>
            <PenLine size={13} /> Write
          </button>
          <button type="button" onClick={() => setPreview(true)} className={`h-8 px-3 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 ${preview ? "bg-white shadow-sm" : "text-[var(--fr-muted)]"}`} data-testid={`${testid}-preview`}>
            <Eye size={13} /> Preview
          </button>
        </div>
      </div>
      {preview ? (
        <div className="prose-fr p-5 min-h-[160px]">{value.trim() ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown> : <p className="text-[var(--fr-muted)]">Nothing to preview yet.</p>}</div>
      ) : (
        <textarea className="w-full min-h-[160px] p-5 font-mono text-[13.5px] leading-relaxed outline-none resize-y bg-white" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Markdown supported" data-testid={`${testid}-input`} />
      )}
    </div>
  );
};

export default function AdminCaseStudyEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { form, setForm, loading, isNew } = useAdminRecord("/admin/case-studies", id, EMPTY);
  const [slugTouched, setSlugTouched] = useState(!(!id || id === "new"));
  const [saving, setSaving] = useState(false);

  const set = (k) => (v) => {
    const val = v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v;
    setForm((f) => ({ ...f, [k]: val, ...(k === "company" && !slugTouched ? { slug: slugify(val) } : {}) }));
  };
  const setMetric = (i, k, v) => setForm((f) => ({ ...f, metrics: f.metrics.map((m, j) => (j === i ? { ...m, [k]: v } : m)) }));
  const setQuote = (k) => (e) => setForm((f) => ({ ...f, quote: { ...f.quote, [k]: e.target.value } }));

  const save = async (status) => {
    if (!form.company.trim() || form.title.trim().length < 3) {
      toast.error("Company and headline are required");
      return;
    }
    setSaving(true);
    const payload = { ...form, status, slug: form.slug || undefined, metrics: form.metrics.filter((m) => m.value.trim() && m.label.trim()) };
    try {
      const { data } = isNew ? await api.post("/admin/case-studies", payload) : await api.put(`/admin/case-studies/${id}`, payload);
      toast.success(status === "published" ? "Case study published" : "Draft saved");
      if (isNew) nav(`/admin/case-studies/${data.id}`, { replace: true });
      else setForm({ ...EMPTY, ...data, metrics: data.metrics.length ? data.metrics : [{ value: "", label: "" }] });
    } catch (e) {
      toast.error(errorMessage(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-[var(--fr-muted)]">Loading…</div>;

  return (
    <div data-testid="admin-case-editor">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="min-w-0">
          <Link to="/admin/case-studies" className="inline-flex items-center gap-1.5 text-sm text-[var(--fr-muted)] hover:text-[var(--fr-ink)]" data-testid="case-editor-back">
            <ArrowLeft size={14} /> All case studies
          </Link>
          <h1 className="font-display font-bold text-2xl md:text-3xl mt-2 truncate">{isNew ? "New case study" : form.company || "Edit case study"}</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isNew && (
            <span className={`chip !text-[11px] uppercase tracking-wide ${form.status === "published" ? "!bg-emerald-50 !text-emerald-700 !border-emerald-100" : "!bg-amber-50 !text-amber-700 !border-amber-100"}`} data-testid="case-editor-status">
              {form.status}
            </span>
          )}
          <button type="button" onClick={() => save("draft")} disabled={saving} className="btn btn-outline btn-sm disabled:opacity-60" data-testid="case-editor-save-draft">
            <Save size={14} className="mr-1.5" /> Save draft
          </button>
          <button type="button" onClick={() => save("published")} disabled={saving} className="btn btn-primary btn-sm disabled:opacity-60" data-testid="case-editor-publish">
            <Send size={14} className="mr-1.5" /> {form.status === "published" && !isNew ? "Update" : "Publish"}
          </button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-5 min-w-0">
          <div className="card p-5 md:p-6 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Company">
                <input className="fr-input" placeholder="Medicina Group of Pharmacies" value={form.company} onChange={set("company")} data-testid="case-company" />
              </Field>
              <Field label="Headline" hint="The result in one line">
                <input className="fr-input" placeholder="45-minute medicine deliveries, every time." value={form.title} onChange={set("title")} data-testid="case-title" />
              </Field>
            </div>
            <Field label="Summary" hint="2–3 sentences shown on cards and at the top of the story">
              <textarea className="fr-input" rows={3} value={form.summary} onChange={set("summary")} data-testid="case-summary" />
            </Field>
          </div>

          <div className="card p-5 md:p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold">Key metrics</h3>
              <span className="text-xs text-[var(--fr-muted)]">Up to 4 · shown on cards and the story hero</span>
            </div>
            <div className="mt-4 space-y-3" data-testid="case-metrics">
              {form.metrics.map((m, i) => (
                <div key={i} className="grid grid-cols-[110px_1fr_36px] gap-2 items-center">
                  <input className="fr-input !h-10 text-sm font-display font-bold" placeholder="6x" value={m.value} onChange={(e) => setMetric(i, "value", e.target.value)} data-testid={`case-metric-value-${i}`} />
                  <input className="fr-input !h-10 text-sm" placeholder="revenue growth" value={m.label} onChange={(e) => setMetric(i, "label", e.target.value)} data-testid={`case-metric-label-${i}`} />
                  <button type="button" className="icon-btn danger" onClick={() => setForm((f) => ({ ...f, metrics: f.metrics.filter((_, j) => j !== i) }))} disabled={form.metrics.length === 1} data-testid={`case-metric-remove-${i}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            {form.metrics.length < 4 && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, metrics: [...f.metrics, { value: "", label: "" }] }))} className="btn btn-outline btn-sm mt-4" data-testid="case-metric-add">
                <Plus size={14} className="mr-1" /> Add metric
              </button>
            )}
          </div>

          {SECTIONS.map(([key, label, hint]) => (
            <MarkdownField key={key} label={label} hint={hint} value={form[key]} onChange={set(key)} testid={`case-${key}`} />
          ))}

          <div className="card p-5 md:p-6 space-y-4">
            <h3 className="font-display font-bold">Customer quote <span className="text-xs font-normal text-[var(--fr-muted)] ml-2">optional</span></h3>
            <textarea className="fr-input" rows={2} placeholder="“We moved 40 vans from WhatsApp dispatch to Fleetroot in a week…”" value={form.quote.text} onChange={setQuote("text")} data-testid="case-quote-text" />
            <div className="grid md:grid-cols-2 gap-4">
              <input className="fr-input !h-10 text-sm" placeholder="Name" value={form.quote.name} onChange={setQuote("name")} data-testid="case-quote-name" />
              <input className="fr-input !h-10 text-sm" placeholder="Role, company" value={form.quote.role} onChange={setQuote("role")} data-testid="case-quote-role" />
            </div>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="card p-5">
            <ImagePicker label="Cover image" value={form.cover} onChange={set("cover")} hint="JPG, PNG or WEBP · 16:10 works best" testid="case-cover" />
          </div>
          <div className="card p-5">
            <ImagePicker label="Customer logo" value={form.logo} onChange={set("logo")} aspect="aspect-[3/1]" fit="object-contain" hint="Transparent PNG preferred" testid="case-logo" />
          </div>
          <div className="card p-5 space-y-4">
            <h3 className="font-display font-bold">Details</h3>
            <Field label="Sector">
              <input className="fr-input !h-10 text-sm" placeholder="Pharmacy" value={form.sector} onChange={set("sector")} data-testid="case-sector" />
            </Field>
            <Field label="Location">
              <input className="fr-input !h-10 text-sm" placeholder="UAE" value={form.location} onChange={set("location")} data-testid="case-location" />
            </Field>
            <Field label="Date">
              <input className="fr-input !h-10 text-sm" type="date" value={form.date} onChange={set("date")} data-testid="case-date" />
            </Field>
            <Field label="URL slug" hint="/case-studies/…">
              <input
                className="fr-input !h-10 text-sm font-mono"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  set("slug")(slugify(e.target.value));
                }}
                data-testid="case-slug"
              />
            </Field>
            <label className="flex items-center gap-3 text-sm font-medium cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-[var(--fr-primary)]" checked={form.featured} onChange={set("featured")} data-testid="case-featured" />
              Featured story
              <span className="text-xs font-normal text-[var(--fr-muted)]">shown first everywhere</span>
            </label>
          </div>

          <div className="card p-5 space-y-4" data-testid="editor-seo-card">
            <h3 className="font-display font-bold">SEO <span className="text-xs font-normal text-[var(--fr-muted)] ml-2">optional</span></h3>
            <Field label="Meta title" hint={`${(form.seoTitle || "").length}/70`}>
              <input className="fr-input !h-10 text-sm" maxLength={70} placeholder="Defaults to the title" value={form.seoTitle || ""} onChange={set("seoTitle")} data-testid="editor-seo-title" />
            </Field>
            <Field label="Meta description" hint={`${(form.seoDescription || "").length}/160`}>
              <textarea className="fr-input text-sm" rows={3} maxLength={160} placeholder="Defaults to the excerpt / summary" value={form.seoDescription || ""} onChange={set("seoDescription")} data-testid="editor-seo-description" />
            </Field>
            <Field label="Social share image URL" hint="defaults to cover">
              <input className="fr-input !h-10 text-sm" placeholder="https://…/image.jpg" value={form.ogImage || ""} onChange={set("ogImage")} data-testid="editor-seo-og-image" />
            </Field>
          </div>
        </aside>
      </div>
    </div>
  );
}
